import {
  Cookies,
  IAddCartItemPayload,
  ICartCheckoutPayload,
  ICartPayload,
  ICartSelection,
  ShopIdentifier
} from '@/client/types'
import { api, protectedApi } from '@/client/utils'
import { Auth } from 'aws-amplify'
import { setCookie } from 'nookies'
const LOCALE_TO_SHOP_ID: Record<string, number> = {
  de: ShopIdentifier.DE,
  uk: ShopIdentifier.UK,
  fr: ShopIdentifier.FR,
  nl: ShopIdentifier.NL,
  it: ShopIdentifier.IT,
  au: ShopIdentifier.AU,
  us: ShopIdentifier.US,
  es: ShopIdentifier.ES,
  pl: ShopIdentifier.PL,
  dk: ShopIdentifier.DK,
  se: ShopIdentifier.SE
}

export const shopId = LOCALE_TO_SHOP_ID[process.env.NEXT_PUBLIC_DEFAULT_LOCALE || 'es'] ?? 10

export const getCartItems = async (cartId: string) => {
  const url = `/cart/getCart/${cartId}`
  const { data } = await api.get<ICartPayload>(url)
  return data
}

export const addCartItem = async (item: ICartSelection, locale: string, cartId?: string) => {
  const url = `/${shopId}/${locale}/cart/addCartItem${cartId ? `/${cartId}` : ''}`
  const { data } = await api.post<IAddCartItemPayload>(url, item)

  if (!cartId) {
    setCookie(null, Cookies.CART_ID, data?._id, {
      maxAge: 30 * 24 * 60 * 60,
      path: '/'
    })
  }
  return data
}

export const addQuoteItemToCart = async (quoteItemId: string, cartId?: string) => {
  const url = `/${shopId}/cart/addQuoteItem/${quoteItemId}${cartId ? `/${cartId}` : ''}`
  const { data } = await api.get<ICartPayload>(url)
  if (!cartId) {
    setCookie(null, Cookies.CART_ID, data?._id, {
      maxAge: 30 * 24 * 60 * 60,
      path: '/'
    })
  }
  return data
}

export const updateCartItem = async (cartId: string, cartItemId: string, payload: Partial<ICartSelection>) => {
  const url = `/cart/${cartId}/cartItem/${cartItemId}`
  const { data } = await api.patch(url, payload)
  return data
}

export const replaceCartItem = async (
  cart: string,
  cartItemId: string,
  payload: Partial<ICartSelection>,
  locale: string
) => {
  const url = `/${shopId}/${locale}/cart/${cart}/cartItem/${cartItemId}`
  const { data } = await api.put(url, payload)
  return data
}

export const deleteCartItem = async (cartId: string, cartItemId: string) => {
  const url = `/cart/${cartId}/cartItem/${cartItemId}`
  const { data } = await api.delete(url)
  return data
}

export const updateCart = async (cartId: string, payload: Partial<ICartPayload>) => {
  const { attributes } = await Auth.currentAuthenticatedUser()
  const url = `${attributes['custom:customer_id']}/cart/${cartId}`
  const { data } = await protectedApi.patch(url, payload)
  return data
}

export const getUserCart = async (cartId?: string) => {
  const { attributes } = await Auth.currentAuthenticatedUser()
  const url = `/cart/${attributes['custom:customer_id']}/getCart`
  const { data } = await protectedApi.get<ICartCheckoutPayload>(url)

  setCookie(null, Cookies.CART_ID, data?._id ? data._id : '', {
    maxAge: 30 * 24 * 60 * 60,
    path: '/'
  })

  return data
}

export const USER_DISABLED_ERROR = 'USER_DISABLED'
export const USER_SESSION_INVALID_ERROR = 'USER_SESSION_INVALID'

/**
 * Verifies the user against Cognito instead of the locally cached tokens.
 * `bypassCache` makes Amplify call Cognito's GetUser, which fails for blacklisted
 * (disabled) users or revoked sessions; Amplify then also signs the user out locally.
 * `currentUserPoolUser` is used because `currentAuthenticatedUser` replaces the Cognito
 * error with 'The user is not authenticated', which hides the 'User is disabled.' reason.
 */
const getVerifiedUser = async () => {
  try {
    return await Auth.currentUserPoolUser({ bypassCache: true })
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error)
    throw new Error(message.includes('User is disabled.') ? USER_DISABLED_ERROR : USER_SESSION_INVALID_ERROR)
  }
}

export const checkoutCart = async (cartId: string) => {
  const { attributes } = await getVerifiedUser()
  const url = `/cart/${attributes['custom:customer_id']}/checkout/${cartId}`
  const { data } = await protectedApi.get<ICartCheckoutPayload>(url)
  return data
}
